import api from '@api';
import EditIcon from '@icons/edit.svg?react';
import FeedbackIcon from '@icons/feedback.svg?react';
import { useChatStore } from '@store/chat';
import { useShellStore } from '@store/shell';
import type { DropdownMenuItemProps } from '@ui/dropdowns';
import { DropdownWithMenuItemsAndIconTrigger } from '@ui/dropdowns';
import { formatChatTitle, log } from '@utils';
import type { FocusEvent, KeyboardEvent } from 'react';
import { startTransition, useEffect, useRef } from 'react';
import Styled from './ActionPanel.styled';

function ActionPanel() {
  const chatStore = useChatStore();
  const shellStore = useShellStore();
  const editableRef = useRef<HTMLSpanElement>(null);
  const isChattingNow = chatStore.chat.messages.length > 0;
  const title = chatStore.titleIsLoading
    ? null
    : chatStore.editedTitle || formatChatTitle(chatStore.chat);

  const handleEdit = () => {
    const range = document.createRange();
    const selection = window.getSelection();

    // Select all the contents (text) of the editable element
    range.selectNodeContents(editableRef.current);

    // Create a temporary input field
    const input = document.createElement('input');
    input.classList.add('temp-virtual-input');
    editableRef.current.parentNode.appendChild(input);

    // Focus the input to trigger the keyboard
    input.focus();

    // Add a slight delay to bypass the 'blur' event issue
    window.setTimeout(() => {
      // Focus the actual editable element
      editableRef.current.focus();
      // Cleanup the temporary input
      input.remove();
    }, 0);

    window.setTimeout(() => {
      // Ensure the cursor is visible by scrolling to the end of the content
      editableRef.current.scroll({
        left: editableRef.current.scrollWidth
      });

      // Clear any existing text selection
      selection.removeAllRanges();

      // Apply the previously created range to the selection
      selection.addRange(range);

      // Collapse the selection so the cursor is placed
      // at the end of the text content
      selection.collapseToEnd();
    }, 10);
  };

  const handleFocus = () => {
    editableRef.current.classList.add('has-focus');
  };

  const handleBlur = (event: FocusEvent<HTMLElement>) => {
    editableRef.current.classList.remove('has-focus');
    editableRef.current.scroll({ left: 0 });

    if (chatStore.chat.id) {
      const title = event.currentTarget.textContent;

      // Save the title only if it's been edited
      if (title !== chatStore.chat.title) {
        api.chats.updateTitleById(chatStore.chat.id, title);
        api.analytics.changedTitle({
          ['new_title']: title,
          ['previous_title']:
            chatStore.editedTitle || formatChatTitle(chatStore.chat),
          ['chat_id']: chatStore.chat.id
        });
        chatStore.setEditedTitle(title);
        log(`Title was changed to '${title}'.`, {
          id: chatStore.chat.id
        });
      }
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    switch (event.code) {
      case 'Enter':
        event.preventDefault();
        editableRef.current.blur();
        break;
      case 'Escape':
        editableRef.current.blur();
        break;
    }
  };

  const handleCopyChatId = async () => {
    // Copy the Chat ID to clipboard
    await navigator.clipboard.writeText(chatStore.chat.id);

    // Display a success toast message
    shellStore.setToast({
      message: 'Chat ID copied to clipboard',
      position: 'fixed',
      status: 'success'
    });
    api.analytics.copiedChatId({ ['chat_id']: chatStore.chat.id });
  };

  const dotsMenuOptions: Array<DropdownMenuItemProps> = [
    {
      label: 'Chat ID',
      value: 'copy_chat_id',
      selected: false,
      startIcon: <FeedbackIcon />,
      onClick: handleCopyChatId
    },
    {
      label: 'Rename',
      value: 'edit_title',
      selected: false,
      startIcon: <EditIcon />,
      onClick: handleEdit
    }
  ];

  useEffect(() => {
    if (
      // 1) After the user input
      chatStore.chat.messages.length > 1 &&
      // 2) Not while waiting for a reply
      !chatStore.isLoading &&
      // 3) If the title is still empty
      !chatStore.editedTitle &&
      // 4) If the chat has just started
      !chatStore.isResumed
    ) {
      chatStore.setTitleIsLoading(true);

      // Notice, a title can be generated while waiting for the insights,
      // but since we do not keep in track when this loading starts,
      // it seems wise to generate it after the loading is completed.
      api.chats.getGeneratedTitleById(chatStore.chat.id).then(
        ({ title }) => startTransition(() => chatStore.setEditedTitle(title)),
        () => chatStore.setTitleIsLoading(false)
      );
    }
  }, [
    chatStore.chat.id,
    chatStore.chat.messages,
    chatStore.editedTitle,
    chatStore.isLoading,
    chatStore.isResumed,
    chatStore.setEditedTitle,
    chatStore.setTitleIsLoading
  ]);

  return (
    <Styled.Wrapper
      className="action-panel"
      hasStartedChat={isChattingNow}>
      <Styled.ChatTitle isVisible={isChattingNow}>
        <Styled.Editable
          data-testid="title"
          onFocus={handleFocus}
          onBlur={handleBlur}
          ref={editableRef}
          isLoading={chatStore.titleIsLoading}
          contentEditable={!(chatStore.titleIsLoading || chatStore.isLoading)}
          enterKeyHint="done"
          suppressContentEditableWarning={true}
          onKeyDown={handleKeyDown}>
          {title}
        </Styled.Editable>
        <DropdownWithMenuItemsAndIconTrigger
          iconTriggerClassName="icon"
          iconTriggerActiveClassName="active"
          options={dotsMenuOptions}
        />
      </Styled.ChatTitle>
    </Styled.Wrapper>
  );
}

export default ActionPanel;
